// Boards (docs/plans/boards.md, B1; Lj's answers Oct 11): a board is a VIEW over tasks. Its columns are a rule over
// fields tasks already have — `task.stage` (the one new field), the review state and `status` — and moving a card
// writes that field through the existing commands. No second copy of the work. Pure — no I/O.
//
//   task.stage   a short slug ('backlog' · 'next' · 'doing' · a board's own: 'idea', 'script' …) or missing.
//                Missing / unknown → the board's first stage column. One per task (not per board), so a card sits in
//                the same place on every board that shows it. Subtasks carry their own (that's a subtask board).
//   Review, Done are never stages: they come from the real state (inReview(t), status === 'done'), so a board can
//                never call a task done that isn't.
//
// A board = { id, name, icon?, template?, columns: [{ id, label, kind: 'stage' | 'review' | 'done', wip? }] }.
// Kept on its owner (department: `dept.boards[]` + `defaultBoardId` + `boardsOn`; project: `project.boards[]`; task:
// `task.board`) — virtual (the template default) until edited. Lj: no "Pass to Claude" column, no Blocked column,
// dropping on Next does NOT plan the task into the week, Done has no cut-off (the screens page it 15 at a time).
import { inReview, reopenTask, handBack, sendBack, approveReview, completeTask, updateTask } from './commands/tasks.js';
import { update, mergeUpdates } from './commands/writes.js';
import { deptRole } from './deptgoals.js';
import { taskDepartmentResolver } from './taskdepartments.js';
import { departmentOf, listDepartments } from './hierarchy.js';
import { noteLinks } from './notes.js';

export const COLUMN_KINDS = Object.freeze(['stage', 'review', 'done']);
/** Stages the default boards use; a board may define its own. */
export const DEFAULT_STAGES = Object.freeze(['backlog', 'next', 'doing']);
/** Done cards shown per page (Lj: "lazy-load 10–15 cards at a time as you scroll"). */
export const DONE_PAGE = 15;
export const STAGE_MAX = 32;
export const LABEL_MAX = 40;
export const WIP_MAX = 99;

const col = (id, label, kind = 'stage', wip = null) => ({ id, label, kind, ...(wip ? { wip } : {}) });

/** The board templates (department settings → Boards; projects and tasks use Kanban until edited). */
export const BOARD_TEMPLATES = Object.freeze({
  dev: Object.freeze({ key: 'dev', name: 'Dev issues', icon: '💻', hint: 'Features, bugs and improvements — Backlog → Next → Doing → Review → Done',
    columns: [col('backlog', 'Backlog'), col('next', 'Next'), col('doing', 'Doing', 'stage', 3), col('review', 'Review', 'review'), col('done', 'Done', 'done')] }),
  content: Object.freeze({ key: 'content', name: 'Content pipeline', icon: '🎬', hint: 'A piece in motion — Idea → Script → Record → Edit → Post',
    columns: [col('idea', 'Idea'), col('script', 'Script'), col('record', 'Record'), col('edit', 'Edit'), col('post', 'Post'), col('done', 'Done', 'done')] }),
  followups: Object.freeze({ key: 'followups', name: 'Follow-ups', icon: '📞', hint: 'To do → Waiting on them → Done',
    columns: [col('todo', 'To do'), col('waiting', 'Waiting on them'), col('done', 'Done', 'done')] }),
  kanban: Object.freeze({ key: 'kanban', name: 'Kanban', icon: '🗂️', hint: 'Backlog → Next → Doing → Review → Done',
    columns: [col('backlog', 'Backlog'), col('next', 'Next'), col('doing', 'Doing'), col('review', 'Review', 'review'), col('done', 'Done', 'done')] }),
});
export const BOARD_TEMPLATE_KEYS = Object.freeze(Object.keys(BOARD_TEMPLATES));

/**
 * Each department's board default, by its role (Lj Oct 11: Dev + Marketing on; Sales, Finance, General, Personal off
 * until turned on; Field has no board). A department with no known role: off, Kanban.
 */
export const DEPARTMENT_BOARD_DEFAULTS = Object.freeze({
  dev: { on: true, template: 'dev' },
  marketing: { on: true, template: 'content' },
  sales: { on: false, template: 'followups' },
  finance: { on: false, template: 'kanban' },
  general: { on: false, template: 'kanban' },
  personal: { on: false, template: 'kanban' },
  field: { on: false, template: null, hidden: true },
});
const OTHER_DEFAULT = { on: false, template: 'kanban' };

const slug = (s) => String(s ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, STAGE_MAX);

/** A board from a template (a fresh copy). `over` replaces fields (name, id, columns). */
export function boardFromTemplate(key, over = {}) {
  const t = BOARD_TEMPLATES[key] || BOARD_TEMPLATES.kanban;
  return cleanBoard({ id: over.id || t.key, name: t.name, icon: t.icon, template: t.key, columns: t.columns.map((c) => ({ ...c })), ...over });
}

/**
 * A board made safe: columns with unique slug ids, known kinds, labels ≤ 40, WIP 1–99 or none; at most one Review
 * and one Done column; at least one stage column (a Backlog is added when none is left).
 */
export function cleanBoard(b) {
  const out = { id: String(b?.id || 'board'), name: String(b?.name || 'Board').trim().slice(0, LABEL_MAX) || 'Board' };
  if (b?.icon) out.icon = String(b.icon).slice(0, 8);
  if (b?.template && BOARD_TEMPLATES[b.template]) out.template = b.template;
  const seen = new Set();
  const kinds = new Set();
  const columns = [];
  for (const c of Array.isArray(b?.columns) ? b.columns : []) {
    const kind = COLUMN_KINDS.includes(c?.kind) ? c.kind : 'stage';
    if (kind !== 'stage' && kinds.has(kind)) continue;
    const id = kind === 'stage' ? slug(c?.id || c?.label) : kind;
    if (!id || seen.has(id) || (kind === 'stage' && COLUMN_KINDS.includes(id) && id !== 'stage')) continue;
    seen.add(id); kinds.add(kind);
    const label = String(c?.label || id).trim().slice(0, LABEL_MAX) || id;
    const wip = Math.round(Number(c?.wip));
    columns.push({ id, label, kind, ...(wip >= 1 ? { wip: Math.min(wip, WIP_MAX) } : {}) });
  }
  if (!columns.some((c) => c.kind === 'stage')) columns.unshift({ id: 'backlog', label: 'Backlog', kind: 'stage' });
  // Review then Done stay at the right end, in that order.
  const rank = (c) => (c.kind === 'done' ? 2 : c.kind === 'review' ? 1 : 0);
  out.columns = columns.map((c, i) => [c, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([c]) => c);
  return out;
}

export const stageColumns = (board) => (board?.columns || []).filter((c) => c.kind === 'stage');
export const firstStage = (board) => stageColumns(board)[0]?.id || 'backlog';

// ── department settings ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * A department's board settings, defaults filled: { role, on, hidden, board, saved, template }.
 * `dept.boardsOn` (true / false) overrides the default; Field is hidden (no board, can't be turned on).
 * The board: `dept.boards` by `defaultBoardId` (else the first), else the role's template (virtual).
 */
export function departmentBoardSettings(dept) {
  const role = deptRole(dept);
  const def = (role && DEPARTMENT_BOARD_DEFAULTS[role]) || OTHER_DEFAULT;
  if (def.hidden) return { role, on: false, hidden: true, board: null, saved: false, template: null };
  const list = Array.isArray(dept?.boards) ? dept.boards.filter((b) => b && Array.isArray(b.columns)) : [];
  const pick = list.find((b) => b.id === dept?.defaultBoardId) || list[0] || null;
  const board = pick ? cleanBoard(pick) : boardFromTemplate(def.template, { id: `${dept?.id || 'general'}-board` });
  const on = typeof dept?.boardsOn === 'boolean' ? dept.boardsOn : def.on;
  return { role, on, hidden: false, board, saved: !!pick, template: board.template || null, defaultTemplate: def.template };
}

/**
 * The department fields to save for a board change (merge them into the department doc):
 *   { on }            turn its board on / off
 *   { template }      replace the board with a template's (keeps the board id)
 *   { wip: { colId: n | null } }   WIP limits
 *   { board }         a whole edited board
 * → { boards, defaultBoardId, boardsOn } (only what changed + the board list when the board changed).
 */
export function departmentBoardPatch(dept, change = {}) {
  const cur = departmentBoardSettings(dept);
  if (cur.hidden) throw new Error(`${dept?.name || 'This department'} has no board`);
  const patch = {};
  if (typeof change.on === 'boolean') patch.boardsOn = change.on;
  let board = null;
  if (change.board) board = cleanBoard({ ...change.board, id: cur.board.id });
  else if (change.template) board = boardFromTemplate(change.template, { id: cur.board.id });
  if (change.wip) {
    const b = board || cur.board;
    board = cleanBoard({ ...b, columns: b.columns.map((c) => (c.id in change.wip ? { ...c, wip: change.wip[c.id] } : c)) });
  }
  if (board) {
    const others = (Array.isArray(dept?.boards) ? dept.boards : []).filter((b) => b && b.id !== board.id);
    patch.boards = [board, ...others];
    patch.defaultBoardId = board.id;
  }
  return patch;
}

/** The departments that show a board (on, not hidden) → [{ dept, board }]. */
export function departmentsWithBoards(departments = []) {
  return listDepartments(departments).map((d) => ({ dept: d, ...departmentBoardSettings(d) })).filter((x) => !x.hidden && x.on).map((x) => ({ dept: x.dept, board: x.board }));
}

// ── scopes: which board, which tasks ───────────────────────────────────────────────────────────────────────────

export const BOARD_SCOPES = Object.freeze(['department', 'project', 'task']);

/**
 * The board for a scope { kind, id } → { board, owner, virtual, hidden?, off? } (owner = the department / project /
 * task doc). Projects and tasks: their saved board, else Kanban (virtual).
 */
export function boardForScope(scope, data = {}) {
  const { departments = [], projects = [], tasks = [] } = data;
  if (scope?.kind === 'department') {
    const dept = listDepartments(departments).find((d) => d.id === scope.id) || null;
    if (!dept) return null;
    const st = departmentBoardSettings(dept);
    return { board: st.board, owner: dept, virtual: !st.saved, hidden: st.hidden, off: !st.on };
  }
  if (scope?.kind === 'project') {
    const p = projects.find((x) => x.id === scope.id) || null;
    if (!p) return null;
    const saved = Array.isArray(p.boards) && p.boards[0] ? cleanBoard(p.boards[0]) : null;
    return { board: saved || boardFromTemplate('kanban', { id: `${p.id}-board`, name: p.name }), owner: p, virtual: !saved };
  }
  if (scope?.kind === 'task') {
    const t = tasks.find((x) => x.id === scope.id) || null;
    if (!t) return null;
    const saved = t.board && Array.isArray(t.board.columns) ? cleanBoard(t.board) : null;
    return { board: saved || boardFromTemplate('kanban', { id: `${t.id}-board`, name: t.title }), owner: t, virtual: !saved };
  }
  return null;
}

const live = (t) => t && !t.deletedAt && !t.bucket;

/**
 * The tasks a scope's board shows (open and done; filters and Done paging are the screen's):
 *   department → its top-level tasks (the task's department; mode off = its project's department), any project;
 *   project → the project's top-level tasks; task → its subtasks.
 * data = { tasks, projects, departments, departmentMode? (false = by project), deptOfTask? }.
 */
export function boardTasks(scope, data = {}) {
  const { tasks = [], projects = [], departments = [] } = data;
  if (scope?.kind === 'task') return tasks.filter((t) => live(t) && t.parentId === scope.id);
  if (scope?.kind === 'project') return tasks.filter((t) => live(t) && !t.parentId && t.projectId === scope.id);
  if (scope?.kind === 'department') {
    let deptOf = data.deptOfTask;
    if (!deptOf) {
      if (data.departmentMode === false) {
        const byId = new Map(projects.map((p) => [p.id, p]));
        deptOf = (t) => departmentOf(byId.get(t.projectId) || null, departments).id;
      } else deptOf = taskDepartmentResolver(tasks, departments, projects);
    }
    return tasks.filter((t) => live(t) && !t.parentId && deptOf(t) === scope.id);
  }
  return [];
}

// ── placing cards ───────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The column a card sits in (first match wins): Done (status done) → Review (inReview) → its stage → the first stage
 * column. A done task on a board with no Done column isn't on it (null); a task in review on a board with no Review
 * column sits in its stage (the card says "in review").
 */
export function placeCard(task, board) {
  const cols = board?.columns || [];
  if (task?.status === 'done') return cols.find((c) => c.kind === 'done')?.id || null;
  if (inReview(task)) { const r = cols.find((c) => c.kind === 'review'); if (r) return r.id; }
  const st = cols.find((c) => c.kind === 'stage' && c.id === task?.stage);
  return st ? st.id : firstStage(board);
}

const dateKey = (t) => t.scheduledDate || '9999-99-99';
const ORDER = {
  // project / task boards: the outline's order
  outline: (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.createdAt || 0) - (b.createdAt || 0),
  // department boards: computed — dated first (soonest), then oldest first. Never a typed priority.
  computed: (a, b) => (dateKey(a) < dateKey(b) ? -1 : dateKey(a) > dateKey(b) ? 1 : 0) || (a.createdAt || 0) - (b.createdAt || 0) || String(a.id).localeCompare(String(b.id)),
};

/**
 * The board's columns with their cards: [{ ...column, cards, count, over, full }].
 *   Done: newest finished first (every one — page it with DONE_PAGE); Review: longest waiting first;
 *   stages: `order` 'outline' (project / task boards) or 'computed' (department boards, the default).
 * WIP: `over` when a column holds more than its limit, `full` at the limit (never blocks a move).
 */
export function boardColumns(board, tasks = [], { order } = {}) {
  const cols = (board?.columns || []).map((c) => ({ ...c, cards: [] }));
  const byId = new Map(cols.map((c) => [c.id, c]));
  for (const t of tasks) { const at = placeCard(t, board); if (at && byId.has(at)) byId.get(at).cards.push(t); }
  const cmp = ORDER[order] || ORDER.computed;
  for (const c of cols) {
    if (c.kind === 'done') c.cards.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
    else if (c.kind === 'review') c.cards.sort((a, b) => (a.review?.at || 0) - (b.review?.at || 0));
    else c.cards.sort(cmp);
    c.count = c.cards.length;
    c.over = !!c.wip && c.count > c.wip;
    c.full = !!c.wip && c.count === c.wip;
  }
  return cols;
}

/** "Doing 3/3" — the column's header count. */
export const wipLabel = (c) => (c.wip ? `${c.count}/${c.wip}` : String(c.count));

// ── filters ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Board filter: { q, tags: [slug] (any of), assignee: 'me' | 'none' | uid, projectId }. Empty = everything.
 * `me` = the signed-in uid.
 */
export function filterCards(tasks = [], f = {}, { me = null } = {}) {
  const q = String(f.q || '').trim().toLowerCase();
  const tags = (f.tags || []).map((x) => String(x).replace(/^#/, '').toLowerCase());
  return tasks.filter((t) => {
    if (q && !`${t.title || ''} ${(t.tags || []).join(' ')}`.toLowerCase().includes(q)) return false;
    if (tags.length && !(t.tags || []).some((x) => tags.includes(String(x).toLowerCase()))) return false;
    if (f.assignee === 'me' && (!me || t.assignee !== me)) return false;
    if (f.assignee === 'none' && t.assignee) return false;
    if (f.assignee && !['me', 'none'].includes(f.assignee) && t.assignee !== f.assignee) return false;
    if (f.projectId && t.projectId !== f.projectId) return false;
    return true;
  });
}

export const isFiltered = (f = {}) => !!(String(f.q || '').trim() || f.tags?.length || f.assignee || f.projectId);

/** The filter chips worth offering: the tags and projects on the board's open cards, most used first. */
export function boardFilterOptions(tasks = [], { limit = 8 } = {}) {
  const tags = new Map(); const projects = new Map(); let mine = false;
  for (const t of tasks) {
    if (t.status === 'done') continue;
    for (const g of t.tags || []) tags.set(g, (tags.get(g) || 0) + 1);
    if (t.projectId) projects.set(t.projectId, (projects.get(t.projectId) || 0) + 1);
    if (t.assignee) mine = true;
  }
  const top = (m) => [...m.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))).slice(0, limit).map(([id, n]) => ({ id, n }));
  return { tags: top(tags), projects: top(projects), assignees: mine };
}

// ── what a card shows ───────────────────────────────────────────────────────────────────────────────────────────

/** taskId → how many live notes link to it (a note links to a card the way it links to a task). */
export function noteCountsByTask(notes = []) {
  const out = new Map();
  for (const n of notes) {
    if (!n || n.deletedAt) continue;
    for (const l of noteLinks(n)) if (l.type === 'task') out.set(l.id, (out.get(l.id) || 0) + 1);
  }
  return out;
}

/** The notes linked to a card, newest first. */
export const notesForCard = (notes = [], taskId) => notes.filter((n) => n && !n.deletedAt && noteLinks(n).some((l) => l.type === 'task' && l.id === taskId))
  .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));

/**
 * The facts a card prints: { subtasks: { done, total } | null, est (min) | null, spentMin, notes, review: 'waiting' |
 * null, reviewAt, sentBack: bool }. `tasks` = every task (for subtask counts), `noteCounts` from noteCountsByTask.
 */
export function cardFacts(task, { tasks = [], noteCounts = null } = {}) {
  const kids = tasks.filter((t) => t.parentId === task.id && !t.deletedAt);
  const lastNote = (task.noteEvents || []).filter((e) => e?.kind === 'review').slice(-1)[0];
  return {
    subtasks: kids.length ? { done: kids.filter((t) => t.status === 'done').length, total: kids.length } : null,
    est: Number(task.estimatedMinutes) > 0 ? Number(task.estimatedMinutes) : null,
    spentMin: Math.round((Number(task.timeSpent) || 0) / 60000),
    notes: noteCounts?.get(task.id) || 0,
    review: inReview(task) ? 'waiting' : null,
    reviewAt: inReview(task) ? task.review.at : null,
    sentBack: !inReview(task) && task.status !== 'done' && !!lastNote && /^Sent back/.test(lastNote.text || ''),
  };
}

// ── moving cards ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * What dropping a card on a column does → { from, to, same, steps: ['reopen' | 'sendBack' | 'stage' | 'handBack' |
 * 'approve' | 'complete'], stage?, note: 'handBack' | 'sendBack' | null }.
 *   → a stage:  reopen it if done, send it back if in review (asks for the note), then stage = the column.
 *   → Review:   reopen if done, then hand it back (one optional line: what was done, where the result is).
 *   → Done:     in review → approve; a task that needs review (approval 'review') → hand it back instead; else complete.
 */
export function planCardMove(task, board, toColumnId) {
  const to = (board?.columns || []).find((c) => c.id === toColumnId);
  if (!to) throw new Error(`no column "${toColumnId}" on ${board?.name || 'this board'}`);
  const from = placeCard(task, board);
  const done = task.status === 'done';
  const review = inReview(task);
  if (from === to.id) return { from, to: to.id, same: true, steps: [], note: null };
  if (to.kind === 'done') {
    if (review) return { from, to: to.id, same: false, steps: ['approve'], note: null };
    if (task.approval === 'review') return { from, to: 'review', same: false, steps: ['handBack'], note: 'handBack', instead: 'review' };
    return { from, to: to.id, same: false, steps: ['complete'], note: null };
  }
  if (to.kind === 'review') return { from, to: to.id, same: false, steps: [...(done ? ['reopen'] : []), 'handBack'], note: 'handBack' };
  const steps = [];
  if (done) steps.push('reopen');
  if (review) steps.push('sendBack');
  steps.push('stage');
  return { from, to: to.id, same: false, steps, stage: to.id, note: review ? 'sendBack' : null };
}

const patchTasks = (tasks, writes) => {
  const by = new Map();
  for (const w of writes) if (w.path.startsWith('tasks/') && w.path.split('/').length === 2) by.set(w.path.slice(6), { ...(by.get(w.path.slice(6)) || {}), ...w.data });
  return tasks.map((t) => (by.has(t.id) ? { ...t, ...by.get(t.id) } : t));
};

/**
 * The drop as writes (one group) — the commands each step names, in order: reopenTask, sendBack, updateTask(stage),
 * handBack, approveReview, completeTask (no time banked — the phone / web complete through their own path, which
 * banks a running timer; see `steps`). ctx = the command ctx (tasks, sections, now, newId, by …).
 * → { writes, plan }.
 */
export function moveCardWrites(ctx, { taskId, board, columnId, note = '', by = null }) {
  const task = (ctx.tasks || []).find((t) => t.id === taskId);
  if (!task) throw new Error(`no task ${taskId}`);
  const plan = planCardMove(task, board, columnId);
  let c = { ...ctx };
  const writes = [];
  const run = (r) => { writes.push(...r.writes); c = { ...c, tasks: patchTasks(c.tasks, r.writes) }; };
  for (const step of plan.steps) {
    if (step === 'reopen') run(reopenTask(c, { id: taskId }));
    else if (step === 'sendBack') run(sendBack(c, { id: taskId, note }));
    else if (step === 'stage') run(updateTask(c, { id: taskId, stage: plan.stage }));
    else if (step === 'handBack') run(handBack(c, { id: taskId, note, by: by || ctx.by || null }));
    else if (step === 'approve') run(approveReview(c, { id: taskId }));
    else if (step === 'complete') run(completeTask(c, { id: taskId }));
  }
  return { writes: mergeUpdates(writes), plan };
}

/** Starting a task's timer moves its card to Doing — from Backlog, Next or no stage. Nothing else moves by itself. */
export const autoDoingStage = (task) => (task && task.status !== 'done' && (!task.stage || task.stage === 'backlog' || task.stage === 'next') ? 'doing' : null);

/** The timer start's stage write, or none. */
export function autoDoingWrites(task, at = Date.now()) {
  const st = autoDoingStage(task);
  return st ? [update(`tasks/${task.id}`, { stage: st, updatedAt: at })] : [];
}

// ── the department Home's Board card ────────────────────────────────────────────────────────────────────────────

/**
 * A department Home's Board card (boards B4 plan, built with B7): its board's columns as counts — Backlog 12 · Next 4 ·
 * Doing 2/3 · Review 1 — WIP over / full flagged, Done = finished in the last `doneDays` days. null when the department
 * has no board on (Field, or switched off). → { board, columns: [{ id, label, kind, count, wip, over, full }], open, max }
 */
export function boardSummary(dept, data = {}, { now = data.now || Date.now(), doneDays = 7 } = {}) {
  const st = departmentBoardSettings(dept);
  if (!dept || st.hidden || !st.on || !st.board) return null;
  const since = now - doneDays * 86400000;
  const tasks = boardTasks({ kind: 'department', id: dept.id }, data).filter((t) => t.status !== 'done' || (Number(t.completedAt) || 0) >= since);
  const columns = boardColumns(st.board, tasks).map(({ cards, ...c }) => ({ id: c.id, label: c.label, kind: c.kind, count: c.count, wip: c.wip || null, over: c.over, full: c.full }));
  return { board: st.board, columns, open: columns.filter((c) => c.kind !== 'done').reduce((a, c) => a + c.count, 0), max: Math.max(1, ...columns.map((c) => c.count)), doneDays };
}
